import KeyFlowVO from 'commons/vo/KeyFlowVO';
import { AssetUrlBuilder, assetWithToken } from 'commons/utils/commonUtil';
import TimeCode from 'commons/utils/TimeCode';
import {
  DEFAULT_SYMBOLIC_PATH,
  File,
  STORAGE_URL,
  STREAMING_URL,
  SystemFolder,
} from 'commons/constants/KeyFlowConstants';
import StorageVO from './StorageVO';
import ConfigVO from './ConfigVO';
import HostFactory from 'commons/handler/HostFactory';

type FileType = {
  KIND: number;
  FILEPATH: string;
  ID: number;
  FILETYPE: number;
  FILENAME: string;
};

type AssetVOProps = {
  ID: number;
  TITLE: string;
  CONTENTS: string;
  CATEGORY_ID: number;
  CAN_LIVE_EDIT: number;
  DURATION_OF_CLIP: number;
  MEDIA_FPS: number;
  MEDIA_FRAME_COUNT: number;
  INGEST_FILE_NAME: string;
  FILE_EXTENSION: string;
  MEDIA_FILE_SIZE: number;
  MEDIA_DATA_RATE: number;
  MEDIA_TRACK: number;
  MEDIA_WIDTH: number;
  MEDIA_HEIGHT: number;
  HAS_TIMECODE: number;
  START_TIMECODE: string;
  END_TIMECODE: string;
  VIDEO_FORMAT: string;
  VIDEO_ASPECT_RATIO: string;
  AUDIO_FORMAT: string;
  AUDIO_CHANNEL: string;
  AUDIO_RATE: number;
  GROUP_ID: number;
  OWNER: string;
  LASTACCESSED: string;
  IS_LOCKED: number;
  IS_COMPLETED: number;
  IS_DELETED: number;
  COUNT_OF_USAGE: number;
  DATE_OF_CREATED: string;
  DATE_OF_MODIFIED: string;
  DATE_OF_DELETED: string;
  DATE_OF_EXPIRED: string;
  DATE_OF_BROADCASTING: string;
  GAME_DATE: string;
  ARCHIVE_STATUS: number;
  BACKUP_STATUS: number;
  RESTORE_STATUS: number;
  DELETOR: string;
  VIDEO_TYPE: number;
  FILE: FileType[];
  SOURCE_FILE_DELETED: number;
  GAME_GROUP_PARENT_VIDEO_ID: number;
  DATE_OF_EXPIRED_HIRES: string;
  IS_GAME_GROUP_CHILD: number;
  TEAM_1: number;
  TEAM_2: number;
  TEAM_1_SCORE: number;
  TEAM_2_SCORE: number;
  GAME_TYPE: number;
};

class AssetVO extends KeyFlowVO {
  id: number;
  title: string;
  contents: string;
  categoryId: number;
  canLiveEdit: number;
  durationOfClip: number;
  mediaFps: number;
  mediaFrameCount: number;
  ingestFileName: string;
  fileExtension: string;
  mediaFileSize: number;
  mediaDataRate: number;
  mediaTrack: number;
  mediaWidth: number;
  mediaHeight: number;
  hasTimecode: number;
  startTimecode: string;
  endTimecode: string;
  videoFormat: string;
  videoAspectRatio: string;
  audioFormat: string;
  audioChannel: string;
  audioRate: number;
  groupId: number;
  owner: string;
  lastaccessed: string;
  isLocked: number;
  isCompleted: number;
  isDeleted: number;
  countOfUsage: number;
  dateOfCreated: string;
  dateOfModified: string;
  dateOfDeleted: string;
  dateOfExpired: string;
  dateOfBroadcasting: string;
  gameDate: string;
  archiveStatus: number;
  backupStatus: number;
  restoreStatus: number;
  deletor: string;
  videoType: number;
  file: FileType[];
  sourceFileDeleted: number;
  gameGroupParentVideoId: number;
  dateOfExpiredHires: string;
  isGameGroupChild: number;
  _symbolicPath: string | undefined;
  team1: number;
  team2: number;
  team1Score: number;
  team2Score: number;
  _storage: StorageVO | null;
  gameType: number;

  constructor(data: AssetVOProps) {
    super(data);
    this.id = data.ID;
    this.title = data.TITLE;
    this.contents = data.CONTENTS;
    this.categoryId = data.CATEGORY_ID;
    this.canLiveEdit = data.CAN_LIVE_EDIT;
    this.durationOfClip = data.DURATION_OF_CLIP;
    this.mediaFps = data.MEDIA_FPS;
    this.mediaFrameCount = data.MEDIA_FRAME_COUNT;
    this.ingestFileName = data.INGEST_FILE_NAME;
    this.fileExtension = data.FILE_EXTENSION;
    this.mediaFileSize = data.MEDIA_FILE_SIZE;
    this.mediaDataRate = data.MEDIA_DATA_RATE;
    this.mediaTrack = data.MEDIA_TRACK;
    this.mediaWidth = data.MEDIA_WIDTH;
    this.mediaHeight = data.MEDIA_HEIGHT;
    this.hasTimecode = data.HAS_TIMECODE;
    this.startTimecode = data.START_TIMECODE;
    this.endTimecode = data.END_TIMECODE;
    this.videoFormat = data.VIDEO_FORMAT;
    this.videoAspectRatio = data.VIDEO_ASPECT_RATIO;
    this.audioFormat = data.AUDIO_FORMAT;
    this.audioChannel = data.AUDIO_CHANNEL;
    this.audioRate = data.AUDIO_RATE;
    this.groupId = data.GROUP_ID;
    this.owner = data.OWNER;

    this.lastaccessed = data.LASTACCESSED;
    this.isLocked = data.IS_LOCKED;
    this.isCompleted = data.IS_COMPLETED;
    this.isDeleted = data.IS_DELETED;
    this.countOfUsage = data.COUNT_OF_USAGE;
    this.dateOfCreated = data.DATE_OF_CREATED;
    this.dateOfModified = data.DATE_OF_MODIFIED;
    this.dateOfDeleted = data.DATE_OF_DELETED;
    this.dateOfExpired = data.DATE_OF_EXPIRED;
    this.dateOfBroadcasting = data.DATE_OF_BROADCASTING;
    this.gameDate = data.GAME_DATE;

    this.archiveStatus = data.ARCHIVE_STATUS;
    this.backupStatus = data.BACKUP_STATUS;
    this.restoreStatus = data.RESTORE_STATUS;
    this.deletor = data.DELETOR;
    this.videoType = data.VIDEO_TYPE;

    this.file = data.FILE;

    this.sourceFileDeleted = data.SOURCE_FILE_DELETED;
    this.gameGroupParentVideoId = data.GAME_GROUP_PARENT_VIDEO_ID;
    this.dateOfExpiredHires = data.DATE_OF_EXPIRED_HIRES;
    this.isGameGroupChild = data.IS_GAME_GROUP_CHILD;
    this.team1 = data.TEAM_1;
    this.team2 = data.TEAM_2;
    this.team1Score = data.TEAM_1_SCORE;
    this.team2Score = data.TEAM_2_SCORE;
    this._storage = null;
    this.gameType = data.GAME_TYPE;
  }

  get symbolicPath(): string {
    return this._symbolicPath || DEFAULT_SYMBOLIC_PATH;
  }

  set symbolicPath(symbolicPath: string) {
    this._symbolicPath = symbolicPath;
  }

  get storage(): StorageVO | null {
    return this._storage;
  }

  set storage(storage: StorageVO | null) {
    this._storage = storage;
  }

  get thumbnailFileName(): string {
    const { file } = this;
    for (const prop in file) {
      if (Object.prototype.hasOwnProperty.call(file, prop)) {
        const { FILETYPE, FILEPATH } = file[prop];
        if (FILETYPE === File.THUMBNAIL) return FILEPATH;
      }
    }
    return '';
  }

  get skimFileName() {
    const { file } = this;
    for (const prop in file) {
      if (Object.prototype.hasOwnProperty.call(file, prop)) {
        const { FILETYPE, FILEPATH } = file[prop];
        if (FILETYPE === File.SKIM) return FILEPATH;
      }
    }
    return '';
  }

  get lowresFileName() {
    const { file } = this;
    for (const prop in file) {
      if (Object.prototype.hasOwnProperty.call(file, prop)) {
        const { FILETYPE, FILEPATH } = file[prop];
        if (FILETYPE === File.LOWRES) return FILEPATH;
      }
    }
    return '';
  }

  get proxyFileName() {
    const { file } = this;
    for (const prop in file) {
      if (Object.prototype.hasOwnProperty.call(file, prop)) {
        const { FILETYPE, FILEPATH } = file[prop];
        if (FILETYPE === File.PROXY) return FILEPATH;
      }
    }
  }

  get lowresSuffixPath() {
    return `${SystemFolder.LOWRES_FOLDER_NAME}/${this.categoryId}/${this.lowresFileName}`;
  }

  get lowresPath(): string {
    const url = `${STORAGE_URL}${this.symbolicPath}/${SystemFolder.LOWRES_FOLDER_NAME}/${this.categoryId}/${this.lowresFileName}`;
    return assetWithToken(url);
  }

  get proxySuffixPath() {
    return `${SystemFolder.PROXY_FOLDER_NAME}/${this.categoryId}/${this.proxyFileName}`;
  }

  get proxyPath() {
    const url = `${STORAGE_URL}${this.symbolicPath}/${SystemFolder.PROXY_FOLDER_NAME}/${this.categoryId}/${this.proxyFileName}`;
    return assetWithToken(url);
  }

  get thumbnailPath() {
    const url = `${STORAGE_URL}${this.symbolicPath}/${SystemFolder.THUMBNAIL_FOLDER_NAME}/${this.categoryId}/${this.thumbnailFileName}`;
    return assetWithToken(url);
  }

  get skimPath() {
    const url = `${STORAGE_URL}${this.symbolicPath}/${SystemFolder.SKIM_FOLDER_NAME}/${this.categoryId}/${this.skimFileName}`;
    return assetWithToken(url);
  }

  get streamingUrl() {
    const url = `${STREAMING_URL}/vod/_definst_/mp4:${this.categoryId}/${this.lowresFileName}/playlist.m3u8`;
    return assetWithToken(url);
  }

  get filePath() {
    const hasProxy = !this.proxySuffixPath.includes('undefined');

    return {
      skim: {
        cloudfront: `${HostFactory.cloudfrontURL}/${SystemFolder.SKIM_FOLDER_NAME}/${this.categoryId}/${this.skimFileName}`,
        nas: this.skimPath,
      },
      thumbnail: {
        cloudfront: `${HostFactory.cloudfrontURL}/${SystemFolder.THUMBNAIL_FOLDER_NAME}/${this.categoryId}/${this.thumbnailFileName}`,
        nas: this.thumbnailPath,
      },
      lowres: {
        cloudfront: `${HostFactory.cloudfrontURL}/${this.lowresSuffixPath}`,
        nas: this.lowresPath,
      },
      ...(hasProxy && {
        proxy: {
          cloudfront: `${HostFactory.cloudfrontURL}/${this.proxySuffixPath}`,
          nas: this.proxyPath,
        },
      }),
    };
  }

  getSkimBackgroundURL() {
    const { cloudfront, nas } = this.filePath.skim;
    return `url(${cloudfront}), url(${nas})`;
  }

  isShowTeam(config: ConfigVO) {
    let result = true;

    const { forcedHideTeam, forcedShowTeam } = config;
    const { team1, team2, gameType } = this;

    if (!forcedHideTeam || !forcedShowTeam) {
      return result;
    }

    const isForcedHide = forcedHideTeam.findIndex(id => id === team1 || id === team2) !== -1;
    const isForcedShow = forcedShowTeam.findIndex(id => id === gameType) !== -1;
    if (isForcedHide && !isForcedShow) {
      result = false;
    }

    return result;
  }
}

export default AssetVO;
